/* eslint-disable @nx/enforce-module-boundaries */
import { Button } from '@mybridge/ui/button';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { FormControl, FormErrorMessage } from '@mybridge/ui/form-control';
import { useToast } from '@mybridge/ui/hooks';
import { HStack, Stack } from '@mybridge/ui/layout';
import { Link } from '@mybridge/ui/link';
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@mybridge/ui/modal';
import { PinInput, PinInputField } from '@mybridge/ui/pin-input';
import { Text } from '@mybridge/ui/text';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  ForgotPasswordOtpSend,
  ForgotPasswordOtpVerify,
  setOpenAuthModal,
  setOpenAuthModalData,
} from 'v4/store/actions/auth.actions';

export const VerifyOtpModal = ({ email: propEmail, onClose, ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslator();
  const [loading, setLoading] = useState(false);
  const { openAuthModalData } = useSelector((state) => state.user) ?? {};
  const email = propEmail || openAuthModalData?.email;

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const toast = useToast({
    position: 'top',
    isClosable: true,
  });

  const onFormSubmit = async (form) => {
    if (!form.code) return;
    setLoading(true);
    try {
      const resp = await dispatch(
        ForgotPasswordOtpVerify({ email, otp: form?.code, code: form?.code })
      );
      const { code, status_code, detail, message } = resp?.payload ?? {};
      const resetToken = resp?.payload?.data?.reset_token || resp?.payload?.reset_token || resp?.payload?.token;
      if (code === 'INVALID_OTP' || status_code >= 400 || resp?.error) {
        toast({
          title:
            detail ||
            message ||
            t('activateAccount.invalidCode') ||
            'Invalid verification code entered! Please try again',
          status: 'error',
        });
        setLoading(false);
        return;
      } else {
        toast({
          title: t('toast.otpVerified') || 'OTP verified successfully!',
          status: 'success',
        });
        onClose?.();
        dispatch(setOpenAuthModalData({ email, otp: form?.code, token: resetToken, reset_token: resetToken }));
        dispatch(setOpenAuthModal('forgot-password-reset'));
      }
    } catch (e) {
      toast({
        title: t('toast.verificationError') || 'Error verifying code!',
        description: e?.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const resp = await dispatch(ForgotPasswordOtpSend({ email }));
      const { code, status_code, email: emailError } = resp?.payload ?? {};
      if (code || status_code === 400) {
        toast({
          title:
            (Array.isArray(emailError) ? emailError[0] : emailError) ||
            t('toast.invalidEmail') ||
            'Error resending OTP',
          status: 'error',
        });
      } else {
        toast({
          title: `${t('toast.resetLinkSent') || 'A reset link has been sent to'} ${email}.`,
          status: 'success',
        });
      }
    } catch (e) {
      toast({
        title: e?.message || 'Error resending OTP',
        status: 'error',
      });
    }
  };

  return (
    <Modal size="lg" isCentered {...props} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius="12px !important">
        <ModalHeader textAlign="center">
          {t('activateAccount.confirmEmail') || 'Confirm your email'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <ModalBody marginLeft="-12px" marginRight="-12px" marginTop="-12px" pb={2}>
            <Stack>
              <Text textAlign="center">
                {t('activateAccount.enterCode') || 'Type in the code we sent to'} {email}
              </Text>
              <HStack marginTop="10px" flex={1} alignItems="flex-start" justifyContent="center">
                <FormControl
                  as={Stack}
                  alignItems="center"
                  justifyContent="center"
                  flex={1}
                  isInvalid={errors?.code}
                >
                  <HStack
                    justifyContent="center"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="5"
                    w="100%"
                    bg="#FFFFFF"
                  >
                    <Controller
                      render={({ field }) => (
                        <PinInput placeholder="-" {...field}>
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                          <PinInputField
                            w={4}
                            border="none"
                            _active={{ border: 'none', outline: 'none' }}
                          />
                        </PinInput>
                      )}
                      control={control}
                      name="code"
                      rules={{ required: true }}
                    />
                  </HStack>
                  {errors?.code && (
                    <FormErrorMessage>
                      {t('activateAccount.validCode') || 'Please enter a valid code'}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </HStack>
              <HStack pt={2} justifyContent="center">
                <Text
                  fontFamily="Inter"
                  fontWeight="500"
                  lineHeight="20.8px"
                  fontSize="16px"
                  color="#5B5B5B"
                >
                  {t('activateAccount.noCode') || 'Didn’t receive the code?'}{' '}
                  <Link
                    onClick={handleResend}
                    display="inline"
                    fontFamily="Inter"
                    fontWeight="500"
                    lineHeight="20.8px"
                    fontSize="16px"
                    color="#5B5B5B"
                  >
                    {t('activateAccount.sendAgain') || 'Send again'}
                  </Link>
                </Text>
              </HStack>
            </Stack>
          </ModalBody>
          <ModalFooter marginLeft="-12px" marginRight="-12px" marginTop="-4px" justifyContent="center">
            <Stack flex={1}>
              <Button
                borderRadius="30px"
                height="48px"
                py={6}
                fontWeight="bold"
                w="100%"
                type="submit"
                variant="primary"
                isLoading={loading}
              >
                {t('activateAccount.agreeConfirm') || 'Agree & Confirm'}
              </Button>
            </Stack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
